import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';

import { StatusFile } from '@/utils/enum';
import { RoomKey, SOCKET_EVENTS, SOCKET_MANAGER_EVENTS } from '@/types';
import { getRoom, socketConfig } from '@/utils/socket';
import { QUERY_KEYS } from '@/utils/query-keys';

import type { BeraJobSummary, BeraJobSummaryFile, BeraVideoPatchStatusEvent } from '@/types/Bera';

interface UseVideoPatchStatusParams {
	organizationId: string;
	companyId: string;
	jobSummary: BeraJobSummary;
}

export type UseVideoPatchStatusResult = {
	isVideoProcessing: boolean;
	isVideoCorrupted: boolean;
	isVideoUnavailable: boolean;
	videoProcessingError: string;
	onPatchRetried: (file: BeraJobSummaryFile) => void;
};

const PROCESSING_STATUSES: StatusFile[] = [StatusFile.IN_QUEUE, StatusFile.PROCESSING];

const NO_PROCESSING_ERROR = '';

export function useVideoPatchStatus({
	organizationId,
	companyId,
	jobSummary
}: UseVideoPatchStatusParams): UseVideoPatchStatusResult {
	const queryClient = useQueryClient();

	const [patchedFile, setPatchedFile] = useState<BeraJobSummaryFile | null>(null);

	const jobSummaryId = jobSummary?.id;

	const summaryFile = jobSummary?.file;

	const summaryFileId = summaryFile?.id;

	const summaryFileStatus = summaryFile?.status;

	const file = patchedFile ?? summaryFile;

	const status = file?.status;

	const isVideoProcessing = !!status && PROCESSING_STATUSES.includes(status);

	const isVideoCorrupted = status === StatusFile.CORRUPTED_FILE;

	const isVideoUnavailable = isVideoProcessing || isVideoCorrupted;

	const videoProcessingError = file?.processing_error ?? NO_PROCESSING_ERROR;

	useEffect(() => {
		setPatchedFile(null);
	}, [summaryFileId, summaryFileStatus]);

	useEffect(() => {
		if (!organizationId || !companyId || !jobSummaryId || !summaryFileId) {
			return;
		}

		function refreshPatchedVideo(): void {
			queryClient.invalidateQueries([QUERY_KEYS.GET_BERA_REPORTS_V2]);
			queryClient.invalidateQueries([QUERY_KEYS.GET_BERA_JOB_SUMMARY]);
			queryClient.invalidateQueries([QUERY_KEYS.GET_DOWNLOAD_VIDEO_URL]);
		}

		function handlePatchStatus(event: BeraVideoPatchStatusEvent): void {
			if (event.bera_job_summary_id !== jobSummaryId) {
				return;
			}

			setPatchedFile(event.file);

			if (event.file.status !== StatusFile.PROCESSED) {
				return;
			}

			refreshPatchedVideo();
		}

		function handleReconnect(): void {
			setPatchedFile(null);
			refreshPatchedVideo();
		}

		const room = getRoom(RoomKey.REPORT_STATUS);
		const config = socketConfig(organizationId, companyId);
		const socket = io(room, config);

		socket.on(SOCKET_EVENTS.BERA_VIDEO_PATCH_STATUS, handlePatchStatus);
		socket.io.on(SOCKET_MANAGER_EVENTS.RECONNECT, handleReconnect);

		return () => {
			socket.io.off(SOCKET_MANAGER_EVENTS.RECONNECT, handleReconnect);
			socket.disconnect();
		};
	}, [organizationId, companyId, jobSummaryId, summaryFileId, queryClient]);

	return {
		isVideoProcessing,
		isVideoCorrupted,
		isVideoUnavailable,
		videoProcessingError,
		onPatchRetried: setPatchedFile
	};
}
